import React, { useState, useEffect, useCallback } from 'react';
import axios from '../../api/axiosSuperAdmin';
import { toast } from '../../components/Toast';
import './SuperAdmin.css';

function UpgradeRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    axios.get('/superadmin/upgrade-requests?status=PENDING')
      .then(res => setRequests(res.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (id) => {
    try {
      await axios.put(`/superadmin/upgrade-requests/${id}/approve`);
      toast.success('Upgrade approved and plan assigned.');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to approve');
    }
  };

  const handleReject = async (id) => {
    try {
      await axios.put(`/superadmin/upgrade-requests/${id}/reject`);
      toast.success('Upgrade request rejected.');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reject');
    }
  };

  if (loading) return null;
  if (requests.length === 0) return null;

  return (
    <div style={{ marginBottom: 32 }}>
      <h2 className="sa-section-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        Plan Upgrade Requests
        {requests.length > 0 && (
          <span style={{ background: '#ef4444', color: '#fff', borderRadius: 12, padding: '2px 8px', fontSize: 12 }}>
            {requests.length}
          </span>
        )}
      </h2>
      {requests.length === 0 ? (
        <p style={{ color: '#6b7280', fontSize: 14 }}>No pending upgrade requests.</p>
      ) : (
        <table className="sa-table">
          <thead>
            <tr><th>Company ID</th><th>Current Plan</th><th>Requested Plan</th><th>Requested At</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {requests.map(r => (
              <tr key={r.id}>
                <td>{r.companyId}</td>
                <td><span className="sa-badge">{r.currentTier}</span></td>
                <td><span className="sa-badge sa-badge-upgrade">{r.requestedTier}</span></td>
                <td>{r.requestedAt ? new Date(r.requestedAt).toLocaleString() : '—'}</td>
                <td style={{ display: 'flex', gap: 8 }}>
                  <button className="sa-btn sa-btn-primary" onClick={() => handleApprove(r.id)}>Approve</button>
                  <button className="sa-btn sa-btn-danger" onClick={() => handleReject(r.id)}>Reject</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function BillingOverview() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [typeFilter, setTypeFilter] = useState('');

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, size: 25 });
      if (typeFilter) params.append('type', typeFilter);
      const res = await axios.get(`/superadmin/billing-events?${params}`);
      const data = res.data.data || res.data;
      setEvents(data.content || data);
      setTotalPages(data.totalPages || 1);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load billing events');
    } finally {
      setLoading(false);
    }
  }, [page, typeFilter]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const formatAmount = (amount) =>
    amount != null ? `₹${Number(amount).toLocaleString('en-IN')}` : '—';

  return (
    <div className="sa-page">
      <h1 className="sa-title">Billing Overview</h1>

      <UpgradeRequests />

      <div className="sa-toolbar">
        <select className="sa-select" value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(0); }}>
          <option value="">All Event Types</option>
          <option value="TRIAL_STARTED">Trial Started</option>
          <option value="UPGRADE">Upgrade</option>
          <option value="DOWNGRADE">Downgrade</option>
          <option value="RENEWAL">Renewal</option>
          <option value="CANCELLATION">Cancellation</option>
        </select>
      </div>

      {loading ? <div className="sa-loading">Loading...</div> : (
        <>
          <table className="sa-table">
            <thead>
              <tr><th>Company</th><th>Event</th><th>Plan</th><th>Amount</th><th>Date</th></tr>
            </thead>
            <tbody>
              {events.length === 0 ? (
                <tr><td colSpan="5" className="sa-empty">No billing events found</td></tr>
              ) : events.map(ev => (
                <tr key={ev.id}>
                  <td>{ev.companyName || ev.companyId}</td>
                  <td><span className={`sa-badge sa-badge-${ev.eventType?.toLowerCase()}`}>{ev.eventType}</span></td>
                  <td>{ev.planTier || '—'}</td>
                  <td>{formatAmount(ev.amount)}</td>
                  <td>{ev.createdAt ? new Date(ev.createdAt).toLocaleDateString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="sa-pagination">
              <button className="sa-btn" onClick={() => setPage(p => p - 1)} disabled={page === 0}>Previous</button>
              <span>Page {page + 1} of {totalPages}</span>
              <button className="sa-btn" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}>Next</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default BillingOverview;
